import { useState, useRef, useEffect, useContext } from 'react';
import Cookies from 'js-cookie';
import { motion } from "framer-motion";
import { Container } from "react-bootstrap";
import Carousel from 'react-bootstrap/Carousel';
import Modal from 'react-bootstrap/Modal';
import CarouselNavHelper from '../../helpers/CarouselNavHelper';
import StudySummary from '../StudySummary/StudySummary';
import PostSurvey from '../PostSurvey/PostSurvey';

import StudyProgressConfig from '../../helpers/StudyProgressBarConfig.js';
import { StudyProgressContext } from '../../Context';

const StudyTwoSummary = () => {
    const [carouselState, setCarouselState] = useState({
        index: 0,
        direction: null,
        carouselItemCount: 2
    });

    const carouselNavHelper = new CarouselNavHelper();
    const [isLastPage, setIsLastPage] = useState(false);
    const [isFirstPage, setIsFirstPage] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [ModalMessage, setModalMessage] = useState('');
    const [ModalTitle, setModalTitle] = useState('');

    const contentRefs = useRef([]);
    const [isNextEnabled, setIsNextEnabled] = useState(false);

    // Create a resize observer to detect when elements are resized
    const resizeObserver = useRef(null);

    const [qualtricsCompleted, setQualtricsCompleted] = useState(false);

    const { studyProgress, setStudyProgress } = useContext(StudyProgressContext);
    const progressByPageConfig = new StudyProgressConfig().getStudyTwoSummaryProgressIndexToProgressMapping();

    const handleCompleteStudyTwoSummary = () => {
        Cookies.set('current_study_two_session', 3, { expires: 7 });
        toggleCarousel('next');
    };

    const handleScroll = (index) => {
        const content = contentRefs.current[index];
        const isEnable = carouselNavHelper.allowNextButton(index, content, true, qualtricsCompleted);
        setIsNextEnabled(isEnable);
    };

    useEffect(() => {
        const content = contentRefs.current[carouselState.index];
    
        // Disconnect any previous observer
        if (resizeObserver.current) {
            resizeObserver.current.disconnect();
        }
    
        // Create a new ResizeObserver to monitor size changes of the content
        resizeObserver.current = new ResizeObserver(() => {
            const content = contentRefs.current[carouselState.index];
            const isEnable = carouselNavHelper.allowNextButton(carouselState.index, content, true, qualtricsCompleted);
            setIsNextEnabled(isEnable);
        });
    
        // Observe the current content
        if (content) {
            resizeObserver.current.observe(content);
        }
    
        return () => {
            if (resizeObserver.current) {
                resizeObserver.current.disconnect();
            }
        };
    }, [carouselState.index, qualtricsCompleted]);

    const handleShowModal = (isShow, title, message) => {
        setShowModal(isShow);

        if (title) setModalTitle(title);
        if (message) setModalMessage(message);
    };

    const toggleCarousel = (direction) => {
        let index = carouselState.index;
        const [min, max] = [0, carouselState.carouselItemCount - 1];
        setIsLastPage(false);
        setIsFirstPage(false);

        if (direction === 'next') {
            index++;
        } else if (direction === 'prev') {
            index--;
        }

        setStudyProgress(progressByPageConfig[index]);

        if (index >= max) {
            index = max;
            setIsLastPage(true);
        }

        if (index <= min) {
            index = min;
            setIsFirstPage(true);
        }

        setCarouselState({
            ...carouselState,
            index,
            direction
        });

        // Reset next button enabled state
        setIsNextEnabled(false);
    };

    return (
        <div className="content">
            <div className="tabcontent">
                <div className="container message-card carousel-container">

                    <Carousel activeIndex={carouselState.index} direction={carouselState.direction} interval={null} controls={false}>
                        <Carousel.Item>
                            <Container className="study-item" style={{ maxHeight: '80vh', overflowY: 'auto' }}
                                ref={(el) => (contentRefs.current[0] = el)} 
                                onScroll={() => handleScroll(0)}
                            >
                                <StudySummary title="Exploration Summary" historyLimit={12} predLimit={7} history_source={"study_two"} />
                            </Container>
                        </Carousel.Item>
                        <Carousel.Item>
                            <Container className="study-item" style={{ maxHeight: '80vh', overflowY: 'auto' }}
                                ref={(el) => (contentRefs.current[1] = el)} 
                                onScroll={() => handleScroll(1)}
                            >
                                <PostSurvey qualtricsCompleted={qualtricsCompleted} onChange={() => setQualtricsCompleted(!qualtricsCompleted)} />
                            </Container>
                        </Carousel.Item>
                    </Carousel>
                    <div className="carousel-button-container">
                        {isFirstPage ? null : (
                            <motion.button
                                type="button"
                                className="button carousel-button"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.8 }}
                                onClick={() => toggleCarousel('prev')}
                            >
                                <i className='bx bx-arrow-back'/>
                                {/* Previous */}
                            </motion.button>
                        )}
                        {isLastPage ? (
                            <motion.button
                                type="button"
                                className={`button carousel-button button-green ${isNextEnabled ? '' : 'button-disabled'}`}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.8 }}
                                onClick={() => handleCompleteStudyTwoSummary()}
                            >
                                Finish
                            </motion.button>
                        ) : (
                            <motion.button
                                type="button"
                                className={`button carousel-button ${isNextEnabled ? '' : 'button-disabled'}`}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.8 }}
                                onClick={() => toggleCarousel('next')}
                                disabled={!isNextEnabled}
                            >
                                <i className='bx bx-arrow-back bx-rotate-180' />
                                {/* Next */}
                            </motion.button>
                        )}
                    </div>

                    <Modal show={showModal} onHide={() => handleShowModal(false)}>
                        <Modal.Header closeButton>
                            <Modal.Title>{ModalTitle}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>{ModalMessage}</Modal.Body>
                        <Modal.Footer>
                            <motion.button
                                type="button"
                                className="button carousel-button"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.8 }}
                                onClick={() => handleShowModal(false)}
                            >
                                Got it!
                            </motion.button>
                        </Modal.Footer>
                    </Modal>

                </div>
            </div>
        </div>
    );
};

export default StudyTwoSummary;
